/** Approval rule levels: labels, defaults, description and payload. */
export const RULE_TYPES = {
  BRANCH_MANAGER: "Branch manager",
  ROLE_SUPERVISOR: "Role supervisor (role tree)",
  ROLE: "Specific role",
  EMPLOYEE: "Specific person",
};
export const SEARCH_LABELS = {
  BRANCH_TREE: "Nearest in the employee's branch tree",
  COMPANY: "Anyone in the company",
};
export const SOURCE = {
  COMPANY: { label: "Company rule", tone: "blue" },
  INHERITED: { label: "Inherited", tone: "gray" },
  DEFAULT: { label: "Default: branch manager", tone: "gray" },
  EMPLOYEE: { label: "Own approver chain", tone: "green" },
  RULE: { label: "Approval rule", tone: "blue" },
};

/** A new level of this type with its default params. */
export const newLevel = (type = "BRANCH_MANAGER") => {
  if (type === "BRANCH_MANAGER") return { type, levelsUp: 0 };
  if (type === "ROLE_SUPERVISOR")
    return { type, levelsUp: 1, search: "BRANCH_TREE" };
  if (type === "ROLE") return { type, roleId: "", search: "COMPANY" };
  return { type, employeeId: "", employee: null };
};

/** The plain choices in the level picker; each one is a type + levelsUp. */
export const LEVEL_CHOICES = [
  {
    key: "BRANCH_MANAGER:0",
    type: "BRANCH_MANAGER",
    levelsUp: 0,
    label: "Their branch manager",
  },
  {
    key: "BRANCH_MANAGER:1",
    type: "BRANCH_MANAGER",
    levelsUp: 1,
    label: "The branch manager one level above",
  },
  {
    key: "BRANCH_MANAGER:2",
    type: "BRANCH_MANAGER",
    levelsUp: 2,
    label: "The branch manager two levels above",
  },
  {
    key: "ROLE_SUPERVISOR:1",
    type: "ROLE_SUPERVISOR",
    levelsUp: 1,
    label: "Their supervisor (by job role)",
  },
  {
    key: "ROLE_SUPERVISOR:2",
    type: "ROLE_SUPERVISOR",
    levelsUp: 2,
    label: "Their supervisor's supervisor",
  },
  { key: "ROLE", type: "ROLE", label: "Anyone with a certain job role" },
  { key: "EMPLOYEE", type: "EMPLOYEE", label: "One specific person" },
];

const UP_TYPES = ["BRANCH_MANAGER", "ROLE_SUPERVISOR"];

/** The picker key of a level ("BRANCH_MANAGER:1", "ROLE"…). */
export const choiceKey = (level) =>
  UP_TYPES.includes(level.type)
    ? `${level.type}:${Number(level.levelsUp) || 0}`
    : level.type;

/** Plain name of a level; levels set further up than the presets get a generic one. */
export const choiceLabel = (level) => {
  const preset = LEVEL_CHOICES.find((c) => c.key === choiceKey(level));
  if (preset) return preset.label;
  const n = Number(level.levelsUp) || 0;
  if (level.type === "BRANCH_MANAGER")
    return `The branch manager ${n} levels above`;
  if (level.type === "ROLE_SUPERVISOR") return `The supervisor ${n} levels up`;
  return RULE_TYPES[level.type] || level.type;
};

/** Choices the server allows, plus the level's own one when it isn't a preset. */
export const choicesFor = (allowedTypes, level) => {
  const list = LEVEL_CHOICES.filter((c) => allowedTypes.includes(c.type));
  if (level && !list.some((c) => c.key === choiceKey(level)))
    list.push({
      key: choiceKey(level),
      type: level.type,
      levelsUp: Number(level.levelsUp) || 0,
      label: choiceLabel(level),
    });
  return list;
};

/** Editor state for a picked choice; keeps the search setting when the type stays. */
export const fromChoice = (key, prev) => {
  const [type, up] = key.split(":");
  const level = newLevel(type);
  if (up !== undefined) level.levelsUp = Number(up);
  if (prev?.type === type && prev.search) level.search = prev.search;
  return level;
};

/** Plain-language description of a level (for the editor preview). */
export const describeLevel = (level, roleTitle) => {
  switch (level.type) {
    case "BRANCH_MANAGER": {
      const n = Number(level.levelsUp) || 0;
      return n === 0
        ? "The manager of the employee's branch. If that branch has none, the manager of the branch above it."
        : `The manager of the branch ${n === 1 ? "one level" : `${n} levels`} above the employee's branch.`;
    }
    case "ROLE_SUPERVISOR": {
      const n = Math.max(1, Number(level.levelsUp) || 1);
      const who =
        n === 1
          ? "Whoever holds the job role the employee's role reports to"
          : `Whoever holds the job role ${n} levels above the employee's role`;
      return `${who}, ${level.search === "COMPANY" ? "anywhere in the company" : "in the employee's branch or the branches above it"}.`;
    }
    case "ROLE":
      return `Anyone with the role ${roleTitle || level.roleTitle || "(choose a role)"}, ${level.search === "BRANCH_TREE" ? "in the employee's branch or the branches above it" : "anywhere in the company"}.`;
    case "EMPLOYEE":
      return level.employee?.name || level.employeeName || "(choose a person)";
    default:
      return level.type;
  }
};

/** Missing params per level, as "Level n: …" (the server validates too). */
export const levelProblems = (levels) =>
  levels.flatMap((l, i) => {
    const p = [];
    if (l.type === "ROLE" && !l.roleId)
      p.push(`Level ${i + 1}: choose a role.`);
    if (l.type === "EMPLOYEE" && !l.employeeId)
      p.push(`Level ${i + 1}: choose a person.`);
    if (l.type === "ROLE_SUPERVISOR" && !(Number(l.levelsUp) >= 1))
      p.push(`Level ${i + 1}: choose how far up the supervisor is.`);
    if (l.type === "BRANCH_MANAGER" && !(Number(l.levelsUp) >= 0))
      p.push(`Level ${i + 1}: choose which branch manager.`);
    return p;
  });

/** PUT body levels: only the params each type takes. */
export const toPayload = (levels) =>
  levels.map((l) => {
    if (l.type === "BRANCH_MANAGER")
      return { type: l.type, levelsUp: Number(l.levelsUp) || 0 };
    if (l.type === "ROLE_SUPERVISOR")
      return {
        type: l.type,
        levelsUp: Number(l.levelsUp) || 1,
        search: l.search || "BRANCH_TREE",
      };
    if (l.type === "ROLE")
      return { type: l.type, roleId: l.roleId, search: l.search || "COMPANY" };
    return { type: l.type, employeeId: l.employeeId };
  });

/** Server levels → editor state. */
export const fromServer = (levels = []) =>
  levels.map((l) => ({
    type: l.type,
    levelsUp: l.levelsUp ?? (l.type === "ROLE_SUPERVISOR" ? 1 : 0),
    search: l.search || (l.type === "ROLE" ? "COMPANY" : "BRANCH_TREE"),
    roleId: l.roleId || "",
    roleTitle: l.roleTitle,
    employeeId: l.employeeId || l.employee?.id || "",
    employee: l.employee || null,
  }));

/** Rules can differ by employee type; "" = the all-employees rules. */
export const EMPLOYEE_TYPES = [
  { value: "", label: "All employees" },
  { value: "Internal", label: "Internal" },
  { value: "Deployed", label: "Deployed" },
];
const typeWord = (t) =>
  t === "Internal"
    ? "internal"
    : t === "Deployed"
      ? "deployed"
      : "all-employees";

/** Where an inherited rule comes from, in words. */
export const inheritedText = (row, companyId) => {
  const from = row.inheritedFrom;
  if (row.source !== "INHERITED" || !from) return null;
  const type = from.employeeType ?? row.appliesTo ?? null;
  if (from.companyId === companyId) return `Using the ${typeWord(type)} rule`;
  return `Using ${from.companyName}'s ${typeWord(type)} rule`;
};
